import {useEffect, useRef} from "react";

// Shared accessibility behavior for Workspace's popup surfaces (right-click
// context menus, the File "backstage" panel, and modal dialogs like
// Properties/Add Account/Template Library) — the Workspace UX audit flagged
// all three as having no focus-trap: keyboard/screen-reader users could tab
// straight through a "modal" into the page behind it, and Escape did nothing.
//
// This hook is purely additive on top of each surface's existing
// click-outside-to-close / X-button-to-close behavior, which is left
// completely unchanged:
//   - Escape closes the surface (calls the same onClose/close function the
//     surface's backdrop-click or X button already calls).
//   - Tab / Shift+Tab cycle only among the surface's own focusable elements
//     instead of escaping into the page behind it.
//   - Focus moves into the surface when it opens, and back to whatever was
//     focused before when it closes, so keyboard users never lose their
//     place.
//
// Usage: give the surface's outer container a ref and tabIndex={-1}, then
// call useModalA11y(isOpen, closeFn, containerRef).
export function useModalA11y(active, onClose, containerRef) {
  // Always call the LATEST onClose the caller passed on its most recent
  // render, not whichever one happened to be in scope when the keydown
  // listener below was attached. The listener-setup effect intentionally
  // only re-runs on [active] (so opening/closing doesn't thrash focus/
  // Tab-trap setup on every unrelated re-render) — but a caller whose
  // onClose closure branches on its own state (e.g. "close a popover
  // first, only close the whole modal if nothing else is open") needs
  // every keypress to see that state's CURRENT value, not the value from
  // whenever the modal first opened. Without this ref, Escape would keep
  // invoking the stale mount-time closure and skip straight to closing
  // the whole surface — reproduced via Playwright in
  // MailMergeFullPreview.js: pressing Escape to close the Edit Merge
  // Field popover closed the entire Preview workspace instead.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!active || !containerRef.current) return;
    const container = containerRef.current;
    const prevActive = document.activeElement;
    const focusables = () =>
      Array.from(
        container.querySelectorAll(
          'button:not([disabled]),[href],input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'
        )
      ).filter(el => el.offsetParent !== null); // skip hidden elements

    const first = focusables()[0];
    (first || container).focus?.();

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onCloseRef.current && onCloseRef.current();
        return;
      }
      if (e.key === "Tab") {
        const items = focusables();
        if (!items.length) return;
        const idx = items.indexOf(document.activeElement);
        if (e.shiftKey && idx <= 0) {
          e.preventDefault();
          items[items.length - 1].focus();
        } else if (!e.shiftKey && idx === items.length - 1) {
          e.preventDefault();
          items[0].focus();
        }
      }
    };
    container.addEventListener("keydown", onKeyDown);
    return () => {
      container.removeEventListener("keydown", onKeyDown);
      if (prevActive && prevActive.focus) prevActive.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);
}

export default useModalA11y;
